import { Box } from '@components/box'
import { Button } from '@components/button/button'
import { Heading } from '@components/heading'
import { Close } from '@components/icons/close'
import { Portal } from '@components/portal'
import { modalStore } from '@lib/store/ui/modal'
import type { ModalSize, ModalState } from '@lib/store/ui/modal'
import { animated } from '@react-spring/web'
import { observer } from 'mobx-react-lite'
import type { FunctionComponent, PropsWithChildren } from 'react'
import type { Aria } from 'react-modal'

import { useModalBounce } from './hooks'
import styles from './modal.module.scss'

/**
 * A window that blocks everything until a certain action is performed.
 */
export const ModalContainer: FunctionComponent<
    PropsWithChildren<ContainerProps>
> = ({
    children,
    visible,
    onHide,
    aria,
    hasCloseButton = true,
    size = 'small',
}) => {
    const { overlay, container } = useModalBounce(visible)

    return (
        <Portal
            ariaHideApp
            shouldCloseOnEsc
            shouldCloseOnOverlayClick
            shouldReturnFocusAfterClose
            isOpen={visible}
            onRequestClose={onHide}
            role='dialog'
            aria={aria}
            overlayElement={(props, children) => (
                <animated.div
                    {...props}
                    style={overlay}
                    className={styles.overlay}
                >
                    {/* @ts-ignore: Fix type check */}
                    {children}
                </animated.div>
            )}
            contentElement={(props, children) => (
                <animated.div
                    {...props}
                    className={styles.container}
                    style={container}
                >
                    {hasCloseButton && (
                        <Button
                            preset='plain'
                            animatePress={false}
                            size='medium'
                            className={styles.close}
                            onClick={onHide}
                        >
                            <Close className={styles.x} />
                        </Button>
                    )}
                    <Box rounded shadow className={styles.dialog} pad={[]}>
                        {/* @ts-ignore: Fix type check */}
                        <div className={styles[size]}>{children}</div>
                    </Box>
                </animated.div>
            )}
        >
            {/* @ts-ignore: Fix type check */}
            {children}
        </Portal>
    )
}

interface ContainerProps {
    size?: ModalSize

    /**
     * Whether the modal is visible.
     */
    visible: boolean

    /**
     * Callback for when the modal is going into hiding 🕵️
     */
    onHide?: () => void

    /**
     * Additional aria attributes for the modal.
     */
    aria?: Aria

    /**
     * Whether to show the X button.
     */
    hasCloseButton?: boolean
}

export const Modal: FunctionComponent<PropsWithChildren<ModalProps>> = ({
    id,
    title = '',
    body = '',
    secondary = '',
    primary = '',
    visible,
    size,
    onHide,
    onPrimary,
    onSecondary,
    hasCloseButton,
    className,
}) => {
    const ids = {
        title: `${id}-title`,
        body: `${id}-body`,
    }

    return (
        <ModalContainer
            visible={visible}
            onHide={onHide}
            size={size}
            aria={{ labelledby: ids.title, describedby: ids.body }}
            hasCloseButton={hasCloseButton}
        >
            <div className={className}>
                <Heading level={4} id={ids.title} className={styles.heading}>
                    {title}
                </Heading>
                <div
                    id={ids.body}
                    className={styles.body}
                    data-testid='modal.body'
                >
                    {body}
                </div>
                <footer className={styles.footer}>
                    {secondary && (
                        <Button
                            size='medium'
                            preset='secondary'
                            onClick={onSecondary}
                            className={styles.secondary}
                            data-testid='modal.secondary'
                        >
                            {secondary}
                        </Button>
                    )}
                    <Button
                        size='medium'
                        preset='primary'
                        onClick={onPrimary}
                        className={styles.primary}
                        data-testid='modal.primary'
                    >
                        {primary}
                    </Button>
                </footer>
            </div>
        </ModalContainer>
    )
}

/**
 * MobX wrapper for the component that toggles it and sets content.
 * This should only be rendered once in the app.
 */
export const ObservedModal: FunctionComponent<{ className?: string }> =
    observer(({ className }) => {
        const { id, content, visible, onHide, onPrimary, onSecondary } =
            modalStore

        return (
            <Modal
                className={className}
                {...content}
                id={id}
                visible={visible}
                onHide={onHide}
                onPrimary={onPrimary}
                onSecondary={onSecondary}
            />
        )
    })

export default ObservedModal

type ModalProps = Omit<ModalState, 'content'> &
    ModalState['content'] & { className?: string }
